/**
 * Reset Password Page
 * Allows users to set a new password after clicking the reset link
 */

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase, signOutCompletely } from "../services/supabase";
import Button from "../components/common/Button";
import Input from "../components/common/Input";
import Card from "../components/ui/Card";

const SESSION_RETRY_DELAYS_MS = [500, 1000, 2000] as const;
const SESSION_INITIALIZATION_TIMEOUT_MS = 8_000;

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [isValidToken, setIsValidToken] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);

  // Check if user has a valid recovery token
  useEffect(() => {
    let active = true;
    let settled = false;
    const timers = new Set<ReturnType<typeof setTimeout>>();

    const schedule = (callback: () => void, delay: number) => {
      const timer = setTimeout(() => {
        timers.delete(timer);
        callback();
      }, delay);
      timers.add(timer);
      return timer;
    };

    const clearTimers = () => {
      timers.forEach((timer) => clearTimeout(timer));
      timers.clear();
    };

    const acceptSession = () => {
      if (!active || settled) {
        return;
      }
      settled = true;
      clearTimers();
      setIsValidToken(true);
      setIsInitializing(false);
    };

    const rejectSession = (message: string) => {
      if (!active || settled) {
        return;
      }
      settled = true;
      clearTimers();
      setError(message);
      setIsValidToken(false);
      setIsInitializing(false);
    };

    // Listen for auth state changes. This callback must return synchronously;
    // awaiting another Supabase operation inside it can block session handling.
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      console.log(
        "[ResetPassword] Auth state change:",
        event,
        session?.user?.id,
      );

      if (session?.user) {
        // Handle all events that provide a valid session
        if (
          event === "INITIAL_SESSION" ||
          event === "SIGNED_IN" ||
          event === "TOKEN_REFRESHED" ||
          event === "PASSWORD_RECOVERY"
        ) {
          console.log("[ResetPassword] Valid session from event:", event);
          acceptSession();
        }
      } else if (event === "SIGNED_OUT") {
        console.log("[ResetPassword] User signed out");
      }
    });

    // Also check if there's an existing session (for page refreshes)
    const checkExistingSession = async () => {
      try {
        const {
          data: { session },
          error: sessionError,
        } = await supabase.auth.getSession();
        if (sessionError) {
          throw sessionError;
        }
        console.log(
          "[ResetPassword] Existing session check:",
          session?.user?.id,
        );

        if (session?.user) {
          acceptSession();
        } else {
          const attemptRetry = async (retryIndex: number) => {
            try {
              const {
                data: { session: retrySession },
                error: retryError,
              } = await supabase.auth.getSession();

              if (retryError) {
                throw retryError;
              }
              if (retrySession?.user) {
                console.log(
                  "[ResetPassword] Session found on retry",
                  retryIndex + 1,
                );
                acceptSession();
              } else if (retryIndex < SESSION_RETRY_DELAYS_MS.length - 1) {
                console.log(
                  "[ResetPassword] No session, retry",
                  retryIndex + 1,
                  "of",
                  SESSION_RETRY_DELAYS_MS.length,
                );
                schedule(
                  () => void attemptRetry(retryIndex + 1),
                  SESSION_RETRY_DELAYS_MS[retryIndex + 1],
                );
              } else {
                console.log("[ResetPassword] No session after all retries");
                rejectSession(
                  "This reset session could not be verified. The link may have expired, already been used, or been opened by an email security scanner. Please request a new reset link and open only the newest email.",
                );
              }
            } catch (retryError) {
              console.error("[ResetPassword] Session retry error:", retryError);
              rejectSession(
                "Failed to verify reset link. Please try again or request a new reset.",
              );
            }
          };

          schedule(() => void attemptRetry(0), SESSION_RETRY_DELAYS_MS[0]);
        }
      } catch (err) {
        console.error("[ResetPassword] Session check error:", err);
        rejectSession(
          "Failed to verify reset link. Please try again or request a new reset.",
        );
      }
    };

    schedule(
      () =>
        rejectSession(
          "Reset-link verification took too long. Please check your connection and request a new reset link.",
        ),
      SESSION_INITIALIZATION_TIMEOUT_MS,
    );
    void checkExistingSession();

    return () => {
      active = false;
      clearTimers();
      subscription.unsubscribe();
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    // Validation
    if (password.length < 8) {
      setError("Password must be at least 8 characters long");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    try {
      setIsLoading(true);

      // Update the user's password
      const { error: updateError } = await supabase.auth.updateUser({
        password: password,
      });

      if (updateError) {
        throw updateError;
      }

      // End the single-purpose recovery session before returning to login.
      // signOutCompletely(), not supabase.auth.signOut(): if that POST 5xx's or times out,
      // supabase-js leaves the token in place and the recovery session survives — after which
      // /login sees a valid session and redirects into the app instead of showing the form.
      await signOutCompletely();

      // Success! Redirect to login
      navigate("/login?reset=success");
    } catch (err: unknown) {
      console.error("Password reset error:", err);
      setError(err instanceof Error ? err.message : "Failed to reset password");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        {/* Logo */}
        <div className="flex justify-center">
          <img
            src="/logowhite.png"
            alt="U.S. LawShield"
            className="h-16 w-auto"
          />
        </div>
        <h2 className="mt-6 text-center text-3xl font-bold text-gray-900">
          Set new password
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Choose a strong password for your account
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <Card padding="lg">
          {isInitializing ? (
            <div className="flex flex-col items-center justify-center py-8 space-y-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
              <p className="text-sm text-gray-600">
                Verifying your reset link...
              </p>
            </div>
          ) : !isValidToken ? (
            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-red-50 border border-red-200">
                <p className="text-sm text-red-800">{error}</p>
              </div>
              <Button
                variant="primary"
                fullWidth
                onClick={() => navigate("/forgot-password")}
              >
                Request New Reset Link
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="p-3 rounded-lg bg-red-50 border border-red-200">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}

              <Input
                type="password"
                label="New Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter new password (min 8 characters)"
                required
                fullWidth
                autoFocus
              />

              <Input
                type="password"
                label="Confirm Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                required
                fullWidth
              />

              <div className="p-3 rounded-lg bg-blue-50 border border-blue-200">
                <p className="text-xs text-blue-800">
                  <strong>Password requirements:</strong>
                  <br />
                  • At least 8 characters long
                  <br />• Contains a mix of letters and numbers (recommended)
                </p>
              </div>

              <Button
                type="submit"
                variant="primary"
                fullWidth
                isLoading={isLoading}
                disabled={isLoading}
              >
                Reset Password
              </Button>
            </form>
          )}
        </Card>
      </div>
    </div>
  );
};

export default ResetPassword;
